import { first } from 'underscore';
import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { DefaultButton, PrimaryButton, Panel, PanelType, Stack } from '@fluentui/react';
import { LegacyEditorialAppealRequestForm, LegacyInlineErrorView } from '@bingads-webui-campaign-react/legacy-editorial-appeal-request-form';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { mergeStyles, fabricPanelFixStyle } from '@bingads-webui-campaign-react/common-styles';

const PANEL_WIDTH = 784;

export const panelStylesProp = {
  footer: {
    ...fabricPanelFixStyle().footer,
  },
  commands: {
    zIndex: 1,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
};

const getStyles = (components, theme = {}) => ({
  uiBlockerAdapter: {
    ...mergeStyles(
      { theme },
      components.reactUIBlockerStyle(theme.palette, 'rgba(255, 255, 255, 0.4)'),
      {
        '& .react-block-ui-none, & .react-block-ui': {
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          overflowY: 'hidden', // to not extend to the content, but shrink to flex-parent
        },
      },
      {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        overflowY: 'hidden',
      }
    ),
  },
});

export const EditorialAppealRequestPanel = ({
  i18n, onSave, onCancel, title, appealDataPromise,
}) => {
  const [appealData, setAppealData] = useState(null);
  const [appealIds, setAppealIds] = useState([]);
  const [isBusy, setIsBusy] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleAppealData = useCallback((aggregatedRejectReasons) => {
    setAppealData(first(aggregatedRejectReasons.appealData));
    setAppealIds(aggregatedRejectReasons.appealIds);
  }, []);
  const handleAppealDataError = useCallback(() => {
    setHasError(true);
  }, []);

  useEffect(() => {
    setIsBusy(true);
    setAppealData(null);
    setAppealIds([]);
    setHasError(false);
    appealDataPromise.then(handleAppealData).catch(handleAppealDataError).finally(() => setIsBusy(false));
  }, [appealDataPromise, handleAppealData, handleAppealDataError]);

  const [editorialAppealFormData, setEditorialAppealFormData] = useState();
  const [isEditorialAppealFormDataValid, setIsEditorialAppealFormDataValid] = useState(false);
  const onEditorialAppealChange = useCallback((data, isValid) => {
    setEditorialAppealFormData(data);
    setIsEditorialAppealFormDataValid(isValid);
  }, []);

  const onSaveClick = useCallback(() => {
    onSave(editorialAppealFormData, appealIds);
  }, [onSave, editorialAppealFormData, appealIds]);

  const onCancelClick = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const classes = useDefaultStyles(getStyles);

  const onRenderFooterContent = useCallback(() => (
    <Stack className="editorial-appeal-request-form-panel-buttons" horizontal tokens={{ childrenGap: 8 }}>
      <Stack.Item key="btn-add">
        <PrimaryButton className="btn-add" onClick={onSaveClick} disabled={hasError || !isEditorialAppealFormDataValid}>
          {i18n.getString(_TL_('Request exception'))}
        </PrimaryButton>
      </Stack.Item>
      <Stack.Item key="btn-cancel">
        <DefaultButton className="btn-cancel" onClick={onCancelClick}>
          {i18n.getString(_TL_('Cancel'))}
        </DefaultButton>
      </Stack.Item>
    </Stack>
  ), [i18n, onSaveClick, onCancelClick, hasError, isEditorialAppealFormDataValid]);


  return (
    <Panel
      className={classNames('editorial-appeal-request-form-panel', classes.uiBlockerAdapter)}
      headerText={title}
      closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
      isOpen
      onDismiss={onCancelClick}
      onRenderFooterContent={onRenderFooterContent}
      isFooterAtBottom
      type={PanelType.custom}
      customWidth={PANEL_WIDTH}
      styles={panelStylesProp}
    >
      <UiBlocker
        blocking={isBusy}
        loadingText={i18n.getString(_TL_('Loading...'))}
      >
        {hasError &&
          <LegacyInlineErrorView />
        }
        {!hasError && appealData &&
          <LegacyEditorialAppealRequestForm
            appealTitle={i18n.getString(_TL_('These asset groups do not meet Microsoft Advertising policies. If you feel your content falls within policy, please submit an appeal and we will happily review the content again.'))}
            appealData={appealData}
            appealIds={appealIds}
            onChange={onEditorialAppealChange}
          />
        }
      </UiBlocker>
    </Panel>
  );
};

EditorialAppealRequestPanel.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  title: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  appealDataPromise: PropTypes.shape({
    then: PropTypes.func.isRequired,
    catch: PropTypes.func.isRequired,
    finally: PropTypes.func.isRequired,
  }).isRequired,
};

EditorialAppealRequestPanel.defaultProps = {
};
